import "./style.scss";

import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { CountdownDisplay } from "@/components/IdentityCheck/IVV/CountdownDisplay";
import { useStartIvv } from "@/components/IdentityCheck/IVV/hooks/useStartIvv";
import { IVVFailed } from "@/components/IdentityCheck/IVV/IVVFailed";
import { useGetWizardEntitlements } from "@/components/IdentityCheck/WizardPathChooser/hooks/useGetWizardEntitlements";
import { useUserProgress } from "@/hooks/useUserProgress";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import IVVAbort from "./IVVAbort";

export const REDIRECT_HASH_STRING = "idv-complete";

/**
 * Convert the timer display to seconds. Prefer this method to allow the timer to remain pure (changes nothing outside
 * its scope). This value is needed by <this> component for submitting metrics so prefer this method using a callback.
 * @visibleForTesting
 */
export const convertTimerDisplayToSeconds = (): undefined | number => {
  const elem = document.body.querySelector(
    '[data-test-id="CountdownTimerDisplay"]'
  );
  if (!elem) {
    return undefined;
  } else {
    if (!elem.textContent) {
      return undefined;
    } else {
      const split = elem.textContent.split(":");
      // "00:10:05" -> sum of 00 hours as seconds, 10 as seconds, 05 as seconds returns 605 seconds.
      return (
        Number(split[0]) * 60 * 60 + Number(split[1]) * 60 + Number(split[2])
      );
    }
  }
};

const TEN_MINUTES_IN_SECONDS = 600;
export const IVV_TIMED_OUT_KEY = "IVV_TIMED_OUT";

/**
 * Override parameters to manipulate component for unit testing different states.
 */
export type IVVServiceIntegrationOverrideParameters = {
  overrideTimedOut?: boolean;
  overrideLoading?: boolean;
  overrideCantLoad?: boolean;
  overrideRejected?: boolean;
  timerOverrideDurationSeconds?: number;
};

/**
 * Used to build the iframe explicit "allow" permissions URL list.
 * This might not be a good candidate for CSP headers since these are needed for development as well as in deployment.
 */
const iframeAllowUrls = [
  "https://beta.idverify.amazon",
  "https://idverify.amazon",
  "https://cdn.withpersona.com",
  "https://amazontest.withpersona.com",
  "https://amazon.withpersona.com",
] as const;

const IVV_IFRAME_ALLOWLIST_SRC = `camera ${iframeAllowUrls.join(
  " "
)}; microphone ${iframeAllowUrls.join(" ")}` as const;

export const DATA_TEST_ID_IVV_SERVICE_INTEGRATION =
  "IVVServiceIntegrationDataTestId";
/**
 * Displays the IVV integration widget.
 *
 * TO TEST:
 *
 * WITHOUT CWB:
 * This URL is valid for 24 hours and must be refreshed daily to be used. To refresh it, you must be in the IVV permission posix group (Which group?), then
 * 1. Go to https://coral.amazon.com/IdentityValidationAndVerificationService/NA/Beta/explorer **using Chrome**
 * 2. Select the Auth Type to be CloudAuth.
 * 3. Set the Request dropdown to EvaluateDocuments
 * 4. Paste the request
 *    ```
 *    {
 *     "Operation": "com.amazon.identityvalidationandverificationservice#EvaluateDocuments",
 *     "Service": "com.amazon.identityvalidationandverificationservice#IdentityValidationAndVerificationService",
 *     "Input": {
 *         "IdentifierValue": "A1Z3LNRQ3NKG70",
 *         "IdentifierType": "CUSTOMER_ID",
 *         "ClientContextId": "amzn1.ivv.clientcontext.123e4567-e89b-12d3-a456-426655440000",
 *         "Attributes": {
 *             "name": {
 *                 "splitName": {
 *                     "encryptedFirstName": "JOHN",
 *                     "encryptedLastName": "SMITH"
 *                 },
 *                 "encryptedFullName": "JOHN SMITH"
 *             }
 *         }
 *       }
 *    }
 *    ```
 * 5. The response should include a EvaluationId, EvaluationStatus, and RedirectURI. The RedirectURI goes in IVV_REDIRECT_URL.
 *    The EvaluationStatus is going to be used to determine if the customer is allowed to use IVV, this will be controlled by CWB in the future and return a useful error for the front end to display.
 *    The EvaluationId will be passed to future web requests.
 * 6. Ensure the user in dynamodb has the column "AutoIdvEligible" marked as true.\
 * 7. Replace `useStartIvv();` with `{data: "urlFromResponse"}` to prevent the website from calling this hook.
 *
 * WITH CWB:
 * 1. This is implemented using the `useStartIvv` hook so loading the component should "just work", however:
 * 2. The back end currently caches IVV verifications so this will only work once per verification before you have to clear the cached verification from DB.
 * 3. Ensure the user in dynamodb has the column "AutoIdvEligible" marked as true.
 */
export const IVVServiceIntegration = (
  props: IVVServiceIntegrationOverrideParameters
): JSX.Element => {
  const { dispatch } = useContext(AppContext);
  const {
    data: IVVIntegrationUrl,
    error: IVVIntegrationUrlError,
    isLoading: IVVIntegrationUrlLoading,
  } = useStartIvv();

  const {
    metadata: wizardEntitlementsMetadata,
    loading: wizardEntitlementsLoading,
    error: wizardEntitlementsError,
  } = useGetWizardEntitlements();

  const { refetch } = useUserProgress();

  // Has the timer elapsed fully?
  const [timedOut, setTimedOut] = useState(props.overrideTimedOut ?? false);
  const [IVVWidgetLoading, setIVVWidgetLoading] = useState(
    props.overrideLoading ?? true
  );

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: "ezo-biometric-in-progress",
    });
  }, []);

  // Listen for anchor link change. This is used for determining if the iframe tried to redirect.
  useEffect(() => {
    trackStructEvent({
      category: Day1EventCategory.AutomatedRivScreen,
      action: Day1EventAction.SectionToggled,
    });

    window.addEventListener("hashchange", hashChangeEventHandler);

    // Remove listener on dismount.
    return () => {
      window.removeEventListener("hashchange", hashChangeEventHandler);
    };
  }, []);

  /**
   * On navigation to changed hash handler
   * @param evt Hash change event
   */
  const hashChangeEventHandler = (evt: HashChangeEvent) => {
    // Handle the event specific for the redirect url.
    /* istanbul ignore next */ // hash change window events are not working in jest
    if (evt.newURL.endsWith(REDIRECT_HASH_STRING)) {
      // Clear hash to prevent it from capturing again if the user reloads the page or something. Also, it's prettier this way.
      location.hash = "";
      trackStructEvent({
        category: Day1EventCategory.AutomatedRivScreen,
        action: Day1EventAction.CompletionRedirect,
        value: convertTimerDisplayToSeconds(),
      });

      // Cause useUserProgress to re-fire. This will recheck back end for verification success. The verification success should be triggered pretty quickly after
      // Persona is finished (Before the actual Continue click on the persona tool). This will cause an internal state transition and hand off
      // rendering responsibility to <IdentityCheck />.
      void refetch();
    }
  };

  const isLoading =
    IVVIntegrationUrlLoading || IVVWidgetLoading || wizardEntitlementsLoading;

  const isError =
    IVVIntegrationUrlError || wizardEntitlementsError || props.overrideCantLoad;

  return (
    <div data-test-id={DATA_TEST_ID_IVV_SERVICE_INTEGRATION}>
      {/* Case where UX is not clear on handling failures yet.*/}
      {(timedOut || props.overrideTimedOut) && (
        <IVVFailed
          failedReason="TIMED_OUT"
          allowManualIdvFallbackOverride={
            wizardEntitlementsMetadata?.isManualIdvEligible
          }
        />
      )}

      {/* Failed to get an integration URL from API. */}
      {isError && (
        <IVVFailed
          failedReason="WIDGET_LOAD_FAILED"
          allowManualIdvFallbackOverride={
            wizardEntitlementsMetadata?.isManualIdvEligible
          }
        />
      )}

      {/* Show Loading if API or Iframe is still loading and NOT in error. */}
      {isLoading && !IVVIntegrationUrlError && (
        <Row justifyContent="center" alignItems="center">
          <Spinner
            dataTestId="IntegrationUrlLoadingSpinner"
            showText
            size={SpinnerSize.Medium}
          />
        </Row>
      )}

      {/* Case when we have an integration URL, have not finished/detected redirect, and timer is not fully elapsed. */}
      {!timedOut &&
        IVVIntegrationUrl &&
        !wizardEntitlementsError &&
        !IVVIntegrationUrlError && (
          <Col>
            <CountdownDisplay
              durationSeconds={
                props.timerOverrideDurationSeconds ?? TEN_MINUTES_IN_SECONDS
              }
              timerFinishedCallback={() => {
                // Track metrics on expiration. This is separate from the Day1EventCategory.AutomatedRivResult metric.
                trackStructEvent({
                  category: Day1EventCategory.AutomatedRivScreen,
                  action: Day1EventAction.ExpiryRedirect,
                  value: convertTimerDisplayToSeconds(),
                });

                localStorage.setItem(IVV_TIMED_OUT_KEY, "1");
                setTimedOut(true);
              }}
            />
            <iframe
              data-test-id="IVVIFrameIntegration"
              title="IVV IFrame"
              src={IVVIntegrationUrl}
              // https://docs.withpersona.com/docs/inquiries-security#iframe-sandbox-attributes
              sandbox="allow-scripts allow-same-origin allow-top-navigation allow-popups allow-forms allow-modals allow-top-navigation-by-user-activation"
              // Permission policy https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Permissions-Policy
              allow={IVV_IFRAME_ALLOWLIST_SRC}
              onLoad={() => {
                /* istanbul ignore next */ // Proving to be very difficult to test on brazil build servers but works on local development env.
                trackStructEvent({
                  category: Day1EventCategory.AutomatedRivScreen,
                  action: Day1EventAction.IVVIframeLoaded,
                });
                // Call back on load to trigger to remove the load.
                /* istanbul ignore next */ // Testing this in Jest doesn't work easily or well.
                setIVVWidgetLoading(false);
              }}
              onError={() => {
                /* istanbul ignore next */ // Proving to be very difficult to test on brazil build servers but works on local development env.
                trackStructEvent({
                  category: Day1EventCategory.AutomatedRivScreen,
                  action: Day1EventAction.IVVIframeLoadError,
                });
                /* istanbul ignore next */ // Testing this in Jest doesn't work easily or well.
                setIVVWidgetLoading(false);
              }}
            />
            <Spacer height="S400" />
            {wizardEntitlementsMetadata?.isManualIdvEligible && <IVVAbort />}
          </Col>
        )}
    </div>
  );
};
